import { Component, Inject, OnInit } from "@angular/core";
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule } from "@angular/forms";
import { MatButtonModule } from "@angular/material/button";
import { MatOptionModule } from "@angular/material/core";
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from "@angular/material/dialog";
import { MatFormFieldModule } from "@angular/material/form-field";
import { MatIconModule } from "@angular/material/icon";
import { MatInputModule } from "@angular/material/input";
import { MatSelect } from "@angular/material/select";
import { RouterModule } from "@angular/router";
import { firstValueFrom, Subject } from "rxjs";
import { MachineOwnerType, MachineOwnerTypeLabels } from "../../../../core/enums/machine-owner-type";
import { MachineType, MachineTypeLabels } from "../../../../core/enums/machine-type";
import { ContractorLookupDto } from "../../../../core/models/contractors/contractor-lookup-dto";
import { MachineSizeLabels } from "../../../../core/models/machines/machine-size";
import { ContractorApiService } from "../../../../core/services/api/contractor-api.service";
import { DefinitionService } from "../../../../core/services/definition/definition.service";

@Component({
    selector: 'app-create-machine-dialog',
    imports: [MatIconModule, MatButtonModule, MatFormFieldModule, MatSelect, MatOptionModule, MatInputModule, FormsModule, ReactiveFormsModule, MatDialogModule, RouterModule],
    templateUrl: './create-machine-dialog.component.html',
    styleUrl: './create-machine-dialog.component.scss',
    providers: [FormBuilder],
    standalone: true
})
export class CreateMachineDialogComponent implements OnInit {
    machineForm: FormGroup;
    isSaving: boolean = false;
    private destroy$ = new Subject<void>();

    allContractors: ContractorLookupDto[] = [];

    machineTypeLabels = MachineTypeLabels
    machineTypeEnum = Object.values(MachineType).filter(x => typeof x === "number");
    ownerTypeLabels = MachineOwnerTypeLabels;
    ownerTypeEnum = Object.values(MachineOwnerType).filter(x => typeof x === "number");

    constructor(@Inject(MAT_DIALOG_DATA) public data: any,
        private fb: FormBuilder,
        public dialogRef: MatDialogRef<CreateMachineDialogComponent>,
        public definitionService: DefinitionService,
        private contractorApiService: ContractorApiService,
    ) {
        this.machineForm = this.fb.group({
            machineType: [null,],
            ownerType: [null,],
            machineManufacturerId: [null,],
            machineManufacturerModelId: [null,],
            machineOwnerId: [null,]
        });

        this.machineForm.get('machineManufacturerId')?.valueChanges.subscribe((selectedManufacturerId: number) => {
            this.definitionService.loadMachineManufacturerModels(selectedManufacturerId);
        });
    }

    async ngOnInit(): Promise<void> {
        this.definitionService.loadMachineManufacturerModels(0);
        this.definitionService.loadMachineManufacturers();
        await this.loadContractors();
    }

    save(): void {
        if (this.machineForm.valid) {
            const mappedForm = this.mapFormToCreateCommand();
            this.dialogRef.close(mappedForm);
        }

        this.isSaving = true;
    }

    mapFormToCreateCommand(): CreateMachineModel {
        const machineModel = this.definitionService.machineManufacturerModels().find(m => m.id === this.machineForm?.get('machineManufacturerModelId')?.value);

        return {
            machineType: this.machineForm?.get('machineType')?.value,
            ownerType: this.machineForm?.get('ownerType')?.value,
            machineManufacturerId: this.machineForm?.get('machineManufacturerId')?.value,
            machineManufacturerModelId: this.machineForm?.get('machineManufacturerModelId')?.value,
            machineSizeText: machineModel ? MachineSizeLabels[machineModel.size] : '',
            machineOwnerId: this.machineForm?.get('ownerType')?.value == MachineOwnerType.External ? this.machineForm?.get('machineOwnerId')?.value : null
        }
    }

    allInputsHaveValue(): boolean {
        if (this.machineForm?.get('ownerType')?.value == MachineOwnerType.Internal) {
            const controlsToValidate = ['machineType', 'ownerType', 'machineManufacturerId', 'machineManufacturerModelId'];
            return controlsToValidate.every(
                controlName => {
                    const control = this.machineForm.get(controlName);
                    return control?.value !== null && control?.value !== '';
                }
            );
        }

        return Object.values(this.machineForm.controls).every(
            control => control.value !== null && control.value !== ''
        );
    }

    cancel(): void {
        this.dialogRef.close(null);
    }

    ngOnDestroy() {
        this.destroy$.next();
        this.destroy$.complete();
    }

    async loadContractors() {
        this.allContractors = await firstValueFrom(this.contractorApiService.getContractorLookups());
    }
}

export interface CreateMachineModel {
    machineType: MachineType;
    ownerType: MachineOwnerType;
    machineManufacturerId: number;
    machineManufacturerModelId: number;
    machineSizeText: string;
    machineOwnerId: number | null;
}